import log from 'loglevel';

import { IChatMessage, IConversation } from '../../types';
import { BaseDBService } from './base.service';

export class ConversationService extends BaseDBService<IConversation> {
  protected storeName = 'conversations';

  private readonly MAX_CONVERSATIONS = 50;
  private readonly MAX_MESSAGES_PER_CONVERSATION = 50;

  async update(conversation: IConversation): Promise<string> {
    conversation.updateAt = Date.now();
    return super.update(conversation);
  }

  async add(conversation: IConversation): Promise<string> {
    const result = await super.add(conversation);

    await this.enforceConversationLimit();

    return result;
  }

  async addMessage(
    conversationId: string,
    message: IChatMessage
  ): Promise<boolean> {
    try {
      const conversation = await this.get(conversationId);
      if (conversation) {
        // Add the new message
        conversation.messages.push(message);

        // Enforce message limit for this conversation - KEEP LATEST MESSAGES
        if (conversation.messages.length > this.MAX_MESSAGES_PER_CONVERSATION) {
          // Remove oldest messages from the beginning to stay within limit
          const messagesToRemove =
            conversation.messages.length - this.MAX_MESSAGES_PER_CONVERSATION;
          conversation.messages.splice(0, messagesToRemove);
          log.info(
            `Removed ${messagesToRemove} oldest messages from conversation ${conversationId} to keep latest ${this.MAX_MESSAGES_PER_CONVERSATION} messages`
          );
        }

        await this.update(conversation);
        return true;
      }
      return false;
    } catch (error) {
      log.error('Failed to add message to conversation:', error);
      return false;
    }
  }

  /**
   * Enforces the maximum conversation limit by removing the oldest conversations
   * KEEPS THE LATEST CONVERSATIONS based on updateAt timestamp
   */
  private async enforceConversationLimit(): Promise<void> {
    try {
      const allConversations = await this.getAll();

      if (allConversations.length > this.MAX_CONVERSATIONS) {
        const sortedConversations = allConversations.sort(
          (a, b) =>
            new Date(b.updateAt).getTime() - new Date(a.updateAt).getTime()
        );

        // Keep only the latest MAX_CONVERSATIONS, remove the rest
        const conversationsToKeep = sortedConversations.slice(
          0,
          this.MAX_CONVERSATIONS
        );
        const conversationsToRemove = sortedConversations.slice(
          this.MAX_CONVERSATIONS
        );

        // Remove the oldest conversations
        for (const conversationToDelete of conversationsToRemove) {
          await this.delete(conversationToDelete.thread.thread_id);
          log.info(
            `Removed oldest conversation "${conversationToDelete.title}" (${conversationToDelete.thread.thread_id}) to keep latest ${this.MAX_CONVERSATIONS} conversations`
          );
        }

        log.info(
          `Enforced conversation limit: removed ${conversationsToRemove.length} oldest conversations, kept ${conversationsToKeep.length} latest conversations`
        );
      }
    } catch (error) {
      log.error('Failed to enforce conversation limit:', error);
    }
  }

  async updateConversationTitle(
    conversationId: string,
    title: string
  ): Promise<void> {
    try {
      const conversation = await this.get(conversationId);
      if (conversation) {
        conversation.title = title;
        await this.update(conversation);
      } else {
        log.warn('Failed to get update conversation title:', conversationId);
      }
    } catch (error) {
      log.error('Failed to get update conversation title:', error);
    }
  }
  /**
   * Gets the current conversation count
   */
  async getConversationCount(): Promise<number> {
    try {
      const all = await this.getAll();
      return all.length;
    } catch (error) {
      log.error('Failed to get conversation count:', error);
      return 0;
    }
  }

  /**
   * Gets the message count for a specific conversation
   */
  async getMessageCount(conversationId: string): Promise<number> {
    try {
      const conversation = await this.get(conversationId);
      return conversation?.messages.length || 0;
    } catch (error) {
      log.error('Failed to get message count:', error);
      return 0;
    }
  }

  async searchConversationsByTitle(
    searchTerm: string
  ): Promise<IConversation[]> {
    try {
      const all = await this.getAll();
      return all.filter((conv) =>
        conv.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } catch (error) {
      log.error('Failed to search conversations by title:', error);
      return [];
    }
  }

  async getRecentConversations(limit: number = 10): Promise<IConversation[]> {
    try {
      const all = await this.getAll();
      return all
        .sort(
          (a, b) =>
            new Date(b.updateAt).getTime() - new Date(a.updateAt).getTime()
        )
        .slice(0, limit);
    } catch (error) {
      log.error('Failed to get recent conversations:', error);
      return [];
    }
  }
}
